import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Filter, RefreshCw, Users, ClipboardList, MailOpen, Activity, Wrench, Save, Plus, Trash2, Palette, Tag, Check, ImagePlus, Send } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import InventoryManager from "@/pages/admin/InventoryManager";
import CommsLog from "@/pages/admin/CommsLog";
import { ServiceRecordButton } from "@/pages/admin/ServiceRecordButton";
import { JobPhotosButton } from "@/pages/admin/JobPhotos";
import { MessageCard } from "@/pages/admin/MessageReply";
import MonthCalendar from "@/pages/admin/MonthCalendar";
import { ServicesEditor, CategoriesManager } from "@/pages/admin/ServicesEditor";
import ThemeManager from "@/pages/admin/ThemeManager";
import GalleryManager from "@/pages/admin/GalleryManager";
import SiteManager from "@/pages/admin/SiteManager";
import CalendarView from "@/pages/admin/WeekCalendar";
import CustomerSearch from "@/pages/admin/CustomerSearch";
import StaffManager from "@/pages/admin/StaffManager";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled", "no_show"];

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const STATUS_STYLE = {
  pending: "border-yellow-700 text-yellow-300 bg-yellow-950/30",
  confirmed: "border-blue-700 text-blue-300 bg-blue-950/30",
  in_progress: "border-orange-600 text-orange-300 bg-orange-950/30",
  completed: "border-green-700 text-green-300 bg-green-950/30",
  cancelled: "border-zinc-700 text-zinc-400 bg-zinc-900",
  no_show: "border-red-800 text-red-300 bg-red-950/40",
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("appointments");
  const { user: authUser } = useAuth();
  const [calView, setCalView] = useState("month");
  const [appts, setAppts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const { themeColor, setColorTheme } = useTheme();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c, m, s, sv, cats, gal] = await Promise.all([
        api.get("/appointments"),
        api.get("/admin/customers"),
        api.get("/contact"),
        api.get("/admin/stats"),
        api.get("/services"),
        api.get("/admin/categories"),
        api.get("/admin/gallery"),
      ]);
      setAppts(a.data);
      setCustomers(c.data);
      setMessages(m.data);
      setStats(s.data);
      setServices(sv.data);
      setCategories(cats.data);
      setGallery(gal.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const titleOne = (id) => services.find((s) => s.id === id)?.title || id;
  const titleFor = (a) => {
    if (a && typeof a === "object") {
      const ids = a.services?.length ? a.services : [a.service];
      return ids.map(titleOne).join(" + ");
    }
    return titleOne(a);
  };

  const filteredAppts = useMemo(() => {
    if (filter === "all") return appts;
    return appts.filter((a) => a.status === filter);
  }, [appts, filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/appointments/${id}/status`, { status });
      setAppts((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success(`Marked ${STATUS_LABEL[status]}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const notifyCustomer = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/admin/appointments/${id}/notify-customer`);
      setAppts((prev) => prev.map((a) => (a.id === id ? data.appointment : a)));
      const sms = data.sms || {};
      const email = data.email || {};

      const bits = [];
      if (email.sent) bits.push("email ✓");
      else if (email.reason === "brevo_ip_not_whitelisted") bits.push("email ✗ (whitelist server IP in Brevo)");
      else if (email.reason === "invalid_api_key") bits.push("email ✗ (bad Brevo API key)");
      else if (email.reason === "sender_not_registered") bits.push("email ✗ (register sender in Brevo)");
      else if (email.reason === "quota_exceeded") bits.push("email ✗ (Brevo quota exceeded)");
      else if (email.reason === "sender_domain_not_verified") bits.push("email ✗ (verify sender domain)");
      else if (email.reason === "no_api_key") bits.push("email skipped (no key)");
      else bits.push(`email ✗ (${email.reason || "failed"})`);

      if (sms.sent) bits.push(`SMS ✓ ${sms.to}`);
      else if (sms.reason === "not_au_mobile") bits.push("SMS skipped (not a mobile)");
      else if (sms.reason === "sms_not_configured") bits.push("SMS not configured");
      else bits.push(`SMS ✗ (${sms.reason || "failed"})`);

      if (email.sent && (sms.sent || sms.reason === "not_au_mobile")) {
        toast.success(`Customer notified — ${bits.join(", ")}`);
      } else {
        toast.warning(`Partial notify — ${bits.join(", ")}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Notify failed");
    }
  }, []);

  const markNoShow = useCallback(async (id, sendSms) => {
    try {
      const { data } = await api.post(`/admin/appointments/${id}/no-show`, { send_sms: !!sendSms });
      setAppts((prev) => prev.map((a) => (a.id === id ? data.appointment : a)));
      const sms = data.sms || {};
      if (sendSms) {
        if (sms.sent) toast.success(`Marked as no-show. SMS sent to ${sms.to} — asking to reschedule.`);
        else if (sms.reason === "not_au_mobile") toast.success("Marked as no-show (phone isn't a mobile — SMS skipped).");
        else toast.warning(`Marked as no-show. SMS failed: ${sms.reason}`);
      } else {
        toast.success("Marked as no-show.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to mark no-show");
    }
  }, []);

  // Auto-trigger notify from admin email deep-link ?notify={apptId}
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const nid = params.get("notify");
    if (!nid || appts.length === 0) return;
    // Only trigger once — clear query param after handling
    notifyCustomer(nid).finally(() => {
      params.delete("notify");
      setParams(params, { replace: true });
    });
  }, [appts.length, notifyCustomer, params, setParams]);

  return (
    <div data-testid="admin-dashboard" className="bg-background min-h-[calc(100vh-4rem)]">
      <Toaster theme="dark" position="top-center" />

      <section className="border-b border-zinc-900 bg-zinc-950/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Admin · Control room</div>
              <h1 className="mt-3 font-display uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                Workshop <span className="text-brand-red">overview.</span>
              </h1>
            </div>
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red text-xs font-mono uppercase tracking-widest">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900">
          <Stat label="Total appts" value={stats?.total_appointments ?? "—"} Icon={ClipboardList} />
          <Stat label="Pending" value={stats?.pending ?? "—"} Icon={Activity} highlight />
          <Stat label="In progress" value={stats?.in_progress ?? "—"} Icon={Activity} />
          <Stat label="Completed" value={stats?.completed ?? "—"} Icon={ClipboardList} />
          <Stat label="Customers" value={stats?.customers ?? "—"} Icon={Users} />
          <Stat label="Messages" value={stats?.messages ?? "—"} Icon={MailOpen} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="border-b border-zinc-900 flex gap-1">
          {[
            { id: "appointments", label: "Appointments" },
            { id: "calendar", label: "Calendar" },
            { id: "inventory", label: "Inventory" },
            { id: "services", label: "Services" },
            { id: "categories", label: "Categories" },
            { id: "site", label: "Site" },
            { id: "gallery", label: "Gallery" },
            { id: "theme", label: "Theme" },
            { id: "customers", label: "Customers" },
            { id: "messages", label: "Messages" },
            { id: "comms", label: "Comms Log" },
            ...(authUser?.role === "admin" ? [{ id: "staff", label: "Staff" }] : []),
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-brand-red text-brand-red" : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {tab === "appointments" && (
          <div>
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <Filter className="h-4 w-4 text-brand-red" />
              <button onClick={() => setFilter("all")} data-testid="filter-all" className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${filter === "all" ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>All</button>
              {STATUSES.map((s) => (
                <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${filter === s ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-10">Loading…</div>
            ) : filteredAppts.length === 0 ? (
              <div data-testid="admin-no-appts" className="border border-zinc-900 bg-zinc-950 p-10 text-center text-zinc-400">No appointments in this view.</div>
            ) : (
              <div className="border border-zinc-900 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950 border-b border-zinc-900">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <th className="p-4">Customer</th>
                      <th className="p-4">Vehicle</th>
                      <th className="p-4">Service</th>
                      <th className="p-4">When</th>
                      <th className="p-4">Deposit</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Notify</th>
                      <th className="p-4">Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppts.map((a) => (
                      <tr key={a.id} data-testid={`admin-appt-${a.id}`} className="border-b border-zinc-900 bg-zinc-950 hover:bg-zinc-900/40">
                        <td className="p-4">
                          <div className="text-white font-medium">{a.user_name}</div>
                          <div className="text-xs text-zinc-500">{a.user_email}</div>
                          {a.user_phone && <div className="text-xs text-zinc-500">{a.user_phone}</div>}
                        </td>
                        <td className="p-4 text-zinc-300">
                          <div>{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}</div>
                          <div className="text-xs text-zinc-500">{a.vehicle_rego ? `${a.vehicle_rego}${a.vehicle_state ? ` (${a.vehicle_state})` : ""}` : "—"}</div>
                        </td>
                        <td className="p-4 text-zinc-300">{titleFor(a)}</td>
                        <td className="p-4 text-zinc-300 font-mono text-xs">
                          <div>{a.preferred_date}</div>
                          <div className="text-zinc-500">{a.preferred_time}</div>
                        </td>
                        <td className="p-4">
                          {a.deposit_status === "paid" ? (
                            <span className="inline-flex items-center px-2 py-0.5 border border-green-700 text-green-400 bg-green-950/30 text-[10px] font-mono uppercase tracking-widest">Paid</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 border border-zinc-700 text-zinc-400 bg-zinc-900 text-[10px] font-mono uppercase tracking-widest">Unpaid</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-1 border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[a.status]}`}>
                            {STATUS_LABEL[a.status] || a.status}
                          </span>
                          {a.customer_confirmed_at && (
                            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 border border-brand-red text-brand-red bg-brand-red/10 text-[10px] font-mono uppercase tracking-widest">
                              <Check className="h-3 w-3" /> Customer confirmed
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <button
                            data-testid={`admin-notify-${a.id}`}
                            onClick={() => notifyCustomer(a.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-red text-white font-mono text-[10px] uppercase tracking-widest hover:bg-[#B91C2B] transition-colors border border-brand-red"
                            title="Confirm this booking and send the customer an email + SMS"
                          >
                            <Send className="h-3 w-3" />
                            {a.customer_notified_at ? "Re-send" : "Confirm & notify"}
                          </button>
                          {a.last_sms?.sent && (
                            <div className="mt-1.5 text-[10px] font-mono text-green-500">SMS ✓ {a.last_sms.to}</div>
                          )}
                          {a.last_sms && !a.last_sms.sent && a.customer_notified_at && (
                            <div className="mt-1.5 text-[10px] font-mono text-zinc-500">
                              {a.last_sms.reason === "not_au_mobile" ? "Email only" : "SMS failed"}
                            </div>
                          )}
                          <div><ServiceRecordButton appt={a} onSaved={load} services={services} /></div>
                          {a.status === "completed" && <div><JobPhotosButton appt={a} /></div>}
                        </td>
                        <td className="p-4">
                          <select
                            data-testid={`admin-status-${a.id}`}
                            value={a.status}
                            onChange={(e) => updateStatus(a.id, e.target.value)}
                            className="bg-black border border-zinc-800 text-white text-xs font-mono uppercase tracking-widest px-2 py-1.5 focus:border-brand-red outline-none"
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "calendar" && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              {[["month", "Month"], ["week", "Week"]].map(([id, label]) => (
                <button key={id} data-testid={`cal-view-${id}`} onClick={() => setCalView(id)}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${calView === id ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
            {calView === "month" ? (
              <MonthCalendar appts={appts} services={services} onNotify={notifyCustomer} onStatus={updateStatus} />
            ) : (
              <CalendarView appts={appts} services={services} onNotify={notifyCustomer} onNoShow={markNoShow} />
            )}
          </div>
        )}

        {tab === "services" && (
          <ServicesEditor
            services={services}
            categories={categories}
            onUpdated={(updated) => setServices((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))}
            onCreated={(created) => setServices((prev) => [...prev, created])}
            onDeleted={(id) => setServices((prev) => prev.filter((s) => s.id !== id))}
          />
        )}

        {tab === "categories" && (
          <CategoriesManager
            categories={categories}
            onCreated={(c) => setCategories((prev) => [...prev, c])}
            onDeleted={(id) => setCategories((prev) => prev.filter((c) => c.id !== id))}
          />
        )}

        {tab === "gallery" && (
          <GalleryManager
            images={gallery}
            onCreated={(img) => setGallery((prev) => [...prev, img])}
            onDeleted={(id) => setGallery((prev) => prev.filter((g) => g.id !== id))}
          />
        )}

        {tab === "site" && <SiteManager />}

        {tab === "inventory" && <InventoryManager />}

        {tab === "comms" && <CommsLog />}

        {tab === "theme" && (
          <ThemeManager currentTheme={themeColor} onChange={setColorTheme} />
        )}

        {tab === "customers" && <CustomerSearch customers={customers} />}

        {tab === "staff" && authUser?.role === "admin" && <StaffManager />}

        {tab === "messages" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {messages.map((m) => (
              <MessageCard key={m.id} msg={m} onReplied={() => load()} />
            ))}
            {messages.length === 0 && (
              <div className="bg-zinc-950 p-10 text-center text-zinc-500 md:col-span-2">No messages yet.</div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

const Stat = ({ label, value, Icon, highlight }) => (
  <div className={`bg-zinc-950 p-5 ${highlight ? "border-l-2 border-brand-red" : ""}`}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <Icon className="h-4 w-4 text-zinc-700" />
    </div>
    <div className={`mt-2 font-display text-3xl ${highlight ? "text-brand-red" : "text-white"}`}>{value}</div>
  </div>
);

